import React, { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Check, ChevronDown, Search, X } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  /** Second line under the label (a phone number, a hint). */
  description?: string;
  /** Group heading; options with the same group are listed together, in first-seen order. */
  group?: string;
  icon?: React.ReactNode;
  avatarUrl?: string;
  /** Letters for the avatar tile when there is no image. */
  initials?: string;
  disabled?: boolean;
  /** Extra text the search also matches (e.g. a code or the raw number). */
  keywords?: string;
}

interface Props {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Show an × that clears the value. Off by default: most fields need a value. */
  clearable?: boolean;
  /** Serial number in front of every option (its position in the list as shown). On by default. */
  numbered?: boolean;
  /** Hide the search box (for short lists). */
  searchable?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
  /** `lg`: taller rows for options with an avatar and a description. `sm`: a compact trigger for filter bars. */
  size?: 'sm' | 'md' | 'lg';
}

const PANEL_GAP = 6;
const PANEL_MAX_H = 320;
const PANEL_MIN_W = 240;

const norm = (s: string) => s.toLowerCase();

/**
 * Searchable dropdown with grouped, numbered rows and a tick on the chosen
 * one. The panel renders in a portal (position: fixed), so it never gets
 * clipped by an `overflow-hidden` card or trapped under a neighbouring one.
 */
export const SearchableSelect = forwardRef<HTMLButtonElement, Props>(({
  options, value, onChange, placeholder = 'Select…', searchPlaceholder = 'Search…', emptyMessage = 'No matches.',
  disabled, invalid, clearable = false, numbered = true, searchable = true, className, id, size = 'md', ...rest
}, ref) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; up: boolean } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement);

  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

  const visible = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return options;
    return options.filter((o) => norm(`${o.label} ${o.description ?? ''} ${o.keywords ?? ''} ${o.group ?? ''}`).includes(q));
  }, [options, query]);

  // Rows in render order, with group headings and the serial number each option shows.
  const rows = useMemo(() => {
    const out: Array<{ kind: 'group'; label: string } | { kind: 'option'; option: SelectOption; n: number; index: number }> = [];
    let lastGroup: string | undefined;
    let n = 0;
    let index = 0;
    for (const o of visible) {
      if (o.group && o.group !== lastGroup) { out.push({ kind: 'group', label: o.group }); lastGroup = o.group; }
      out.push({ kind: 'option', option: o, n: ++n, index: index++ });
    }
    return out;
  }, [visible]);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - PANEL_GAP;
    const up = below < Math.min(PANEL_MAX_H, 220) && r.top > below;
    // The panel is at least 240px wide; a narrow trigger near the right edge would push it off screen.
    const width = Math.min(Math.max(r.width, PANEL_MIN_W), window.innerWidth - 2 * PANEL_GAP);
    const left = Math.max(PANEL_GAP, Math.min(r.left, window.innerWidth - width - PANEL_GAP));
    setPos({ top: up ? r.top - PANEL_GAP : r.bottom + PANEL_GAP, left, width, up });
  }, []);

  const close = useCallback(() => { setOpen(false); setQuery(''); }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const idx = Math.max(0, visible.findIndex((o) => o.value === value));
    setHighlight(idx);
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    const onScroll = (e: Event) => { if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return; place(); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', place);
    return () => { window.clearTimeout(t); window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', place); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const el = panelRef.current?.querySelector<HTMLElement>(`[data-index="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const pick = (o: SelectOption) => {
    if (o.disabled) return;
    onChange(o.value);
    close();
    triggerRef.current?.focus();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); }
      return;
    }
    const visibleSelectable = rows.filter((r): r is Extract<typeof r, { kind: 'option' }> => r.kind === 'option' && !r.option.disabled);
    const current = visibleSelectable.findIndex((r) => r.index === highlight);
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(visibleSelectable[Math.min(visibleSelectable.length - 1, current + 1)]?.index ?? 0); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(visibleSelectable[Math.max(0, current - 1)]?.index ?? 0); }
    else if (e.key === 'Home') { e.preventDefault(); setHighlight(visibleSelectable[0]?.index ?? 0); }
    else if (e.key === 'End') { e.preventDefault(); setHighlight(visibleSelectable[visibleSelectable.length - 1]?.index ?? 0); }
    else if (e.key === 'Enter') { e.preventDefault(); const r = visibleSelectable.find((x) => x.index === highlight); if (r) pick(r.option); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); triggerRef.current?.focus(); }
    else if (e.key === 'Tab') close();
  };

  const tall = size === 'lg';

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-label={rest['aria-label']}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={onKey}
        // The global :focus-visible outline (unlayered, 2px offset) would draw a second box around this ring.
        style={{ outline: 'none' }}
        className={clsx(
          'flex w-full items-center gap-2.5 rounded-lg border bg-surface text-left transition-colors duration-150',
          tall ? 'h-12 px-3.5 text-sm' : size === 'sm' ? 'h-8 px-2.5 text-xs' : 'h-10 px-3.5 text-sm',
          'hover:border-border-strong focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft',
          'disabled:cursor-not-allowed disabled:opacity-60',
          invalid ? 'border-danger' : open ? 'border-primary ring-4 ring-primary-soft' : 'border-border',
          className,
        )}
      >
        {selected ? (
          <>
            <OptionVisual option={selected} size={tall ? 32 : 22} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-text-primary">{selected.label}</span>
              {tall && selected.description && <span className="block truncate text-xs text-text-muted">{selected.description}</span>}
            </span>
          </>
        ) : (
          <span className="min-w-0 flex-1 truncate text-text-muted">{placeholder}</span>
        )}
        {clearable && value && !disabled && (
          <span
            role="button"
            aria-label="Clear"
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-text-muted hover:bg-surface-alt hover:text-text-primary"
          >
            <X size={12} />
          </span>
        )}
        <ChevronDown size={16} className={clsx('shrink-0 text-text-muted transition-transform duration-200', open && 'rotate-180')} />
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', left: pos.left, width: pos.width, ...(pos.up ? { bottom: window.innerHeight - pos.top } : { top: pos.top }) }}
          className="z-[70] overflow-hidden rounded-xl border border-border bg-surface shadow-elevated animate-fade-in"
          onKeyDown={onKey}
        >
          {searchable && (
            <div className="flex items-center gap-2 border-b border-border bg-surface-alt/40 px-3 transition-colors focus-within:border-primary/50 focus-within:bg-surface">
              <Search size={14} className="shrink-0 text-text-muted" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                // The row's border shows focus; the global offset outline drew a stray gold box here.
                style={{ outline: 'none', boxShadow: 'none' }}
                className="h-10 w-full border-0 bg-transparent p-0 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-0"
              />
            </div>
          )}
          <ul id={listId} role="listbox" tabIndex={-1} aria-activedescendant={`${listId}-${highlight}`} className="max-h-[320px] overflow-y-auto p-1" style={{ maxHeight: PANEL_MAX_H }}>
            {rows.length === 0 && <li className="px-3 py-6 text-center text-sm text-text-muted">{emptyMessage}</li>}
            {rows.map((r) => r.kind === 'group' ? (
              <li key={`g-${r.label}`} className="flex items-center gap-2 px-2.5 pb-1 pt-3 first:pt-2">
                <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-primary/80">{r.label}</span>
                <span className="h-px flex-1 bg-gradient-to-r from-border to-transparent" />
              </li>
            ) : (
              <li
                key={r.option.value}
                id={`${listId}-${r.index}`}
                role="option"
                aria-selected={r.option.value === value}
                aria-disabled={r.option.disabled || undefined}
                data-index={r.index}
                onMouseEnter={() => !r.option.disabled && setHighlight(r.index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(r.option)}
                className={clsx(
                  'flex items-center gap-2.5 rounded-lg px-2.5 transition-colors',
                  tall ? 'py-2' : 'py-1.5',
                  r.option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                  r.index === highlight && !r.option.disabled && 'bg-primary-soft',
                  r.option.value === value && 'font-semibold text-primary',
                )}
              >
                {numbered && (
                  <span className="flex h-5 min-w-[22px] shrink-0 items-center justify-center rounded-md px-1 text-[11px] font-semibold tabular-nums"
                    style={{ color: 'inherit', background: 'color-mix(in srgb, currentColor 9%, transparent)', boxShadow: 'inset 0 0 0 1px color-mix(in srgb, currentColor 16%, transparent)', opacity: 0.85 }}>
                    {r.n}
                  </span>
                )}
                <OptionVisual option={r.option} size={tall ? 32 : 22} />
                <span className="min-w-0 flex-1">
                  <span className={clsx('block truncate text-sm', r.option.value === value ? 'text-primary' : 'text-text-primary')}>{r.option.label}</span>
                  {r.option.description && <span className="block truncate text-xs font-normal text-text-muted">{r.option.description}</span>}
                </span>
                <Check size={15} className={clsx('shrink-0 text-primary transition-opacity', r.option.value === value ? 'opacity-100' : 'opacity-0')} />
              </li>
            ))}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
});
SearchableSelect.displayName = 'SearchableSelect';

const OptionVisual: React.FC<{ option: SelectOption; size: number }> = ({ option, size }) => {
  if (option.avatarUrl) return <img src={option.avatarUrl} alt="" className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />;
  if (option.initials) {
    return (
      <span className="flex shrink-0 items-center justify-center rounded-full bg-gold-gradient-soft font-bold text-primary" style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}>
        {option.initials}
      </span>
    );
  }
  if (option.icon) return <span className="flex shrink-0 items-center justify-center text-text-muted" style={{ width: Math.min(size, 20) }}>{option.icon}</span>;
  return null;
};
